import React, { useEffect, useMemo } from 'react';
import './OverviewStatCard.css';
import { useApi } from '../services/apiService';
import { Account } from '../types/Account';
import { Summary } from '../types/Summary';
import { formatMoney } from '../utils/format';

interface OverviewStatCardProps {
  accounts: Account[];
}

const OverviewStatCard: React.FC<OverviewStatCardProps> = ({ accounts }) => {
  const [summaries, setSummaries] = React.useState<Summary[] | null>(null);
  const [recordDateNz, setRecordDateNz] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const { getFinancialSummary } = useApi();

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const data = await getFinancialSummary();
        setSummaries(data.items);
        setRecordDateNz(data.recordDateNz ?? null);
      } catch (err) {
        setError('Failed to fetch account data.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummaries();
  }, []);

  // Per-currency subtotals, in each account's own currency.
  const currencyTotals = useMemo(() => {
    const totals = new Map<string, { amount: number; count: number }>();
    accounts.forEach((account) => {
      const currency = account.balance.currency;
      const entry = totals.get(currency) ?? { amount: 0, count: 0 };
      entry.amount += Number(account.balance.current) || 0;
      entry.count += 1;
      totals.set(currency, entry);
    });
    return Array.from(totals.entries()).sort((a, b) =>
      a[0] === 'NZD' ? -1 : b[0] === 'NZD' ? 1 : b[1].amount - a[1].amount,
    );
  }, [accounts]);

  return (
    <section className="overview-stat-card" aria-label="Financial summary">
      <div className="stat-card stat-card-hero">
        <div className="stat-card-header">
          <span className="stat-label">Total balance</span>
          <span className="stat-badge">NZD</span>
        </div>
        {summaries ? (
          summaries.map((summary) => (
            <div className="stat-value stat-value-hero num" key={summary.name}>
              {formatMoney(summary.amount, 0)}
            </div>
          ))
        ) : (
          <div className={error ? 'error' : 'muted'}>
            {loading ? 'Loading...' : (error ?? 'No data available.')}
          </div>
        )}
        <div className="stat-meta">
          {recordDateNz ? `As at ${recordDateNz} · ` : ''}All currencies
          converted to NZD
        </div>
      </div>
      {currencyTotals.map(([currency, { amount, count }]) => (
        <div className="stat-card" key={currency}>
          <span className="stat-label">{currency}</span>
          <div className="stat-value num">{formatMoney(amount)}</div>
          <div className="stat-meta">
            {count} account{count === 1 ? '' : 's'}
          </div>
        </div>
      ))}
    </section>
  );
};

export default OverviewStatCard;
